/**
 * ProspectMainViewer Component
 * Main view mode for displaying prospect information
 */

import type { ProspectResponse, TeamInfo } from "../../../../types/api";
import type { DefaultTagDefinition } from "../../../../types/tags";
import { StarIcon } from "../../../../assets/StarIcon";
import { EditIcon } from "../../../../assets/EditIcon";
import { ModalCloseButton } from "../../../../components/ModalCloseButton";
import { ProspectProfileSection } from "./ProspectProfileSection";
import { ProspectVideosSection } from "./ProspectVideosSection";
import { ProspectDocsSection } from "./ProspectDocsSection";
import { ProspectAboutSection } from "./ProspectAboutSection";
import type { PitchConfig } from "../../../../constants/pitchConfig";
import {
  formatEligibilityClass,
  formatJucoYear,
} from "../../../../utils/roster-class";
import { isJucoPlayerLevel } from "../../../../constants/playerLevels";

import type { RosterSport } from "../../../../constants/sport";

export type ProspectSection = "profile" | "videos" | "notes" | "about";

interface ProspectMainViewerProps {
  prospect: ProspectResponse;
  activeSection: ProspectSection;
  onSectionChange: (section: ProspectSection) => void;
  onFavoriteToggle: () => void;
  onEditClick: () => void;
  onClose: () => void;
  // Profile section props
  activeMetricsTab: "positionPlayer" | "pitcher";
  onMetricsTabChange: (tab: "positionPlayer" | "pitcher") => void;
  tagDefinitions: DefaultTagDefinition[];
  tagsByName: Record<string, string[]>;
  onTagPress: (tagName: string) => void;
  tagError: string | null;
  metricsError: string | null;
  onPositionPlayerMetricUpdate: (field: string, value: number | null) => void;
  onPitcherMetricUpdate: (field: string, value: number | null) => void;
  pitchConfigs: PitchConfig[];
  currentUserId?: string;
  sport?: string | null;
  /**
   * The organization's sport, normalised the way ProspectViewerPanel reads it (impersonation
   * override first, else the coach's own org). Gates the baseball-only links section.
   */
  rosterSport: RosterSport;
  // About section props
  onJerseyClick: (team: TeamInfo) => void;
  onRemoveTeam?: (teamId: string) => void;
  onAddTeam?: () => void;
}

export function ProspectMainViewer({
  prospect,
  activeSection,
  onSectionChange,
  onFavoriteToggle,
  onEditClick,
  onClose,
  activeMetricsTab,
  onMetricsTabChange,
  tagDefinitions,
  tagsByName,
  onTagPress,
  tagError,
  metricsError,
  onPositionPlayerMetricUpdate,
  onPitcherMetricUpdate,
  pitchConfigs,
  currentUserId,
  sport,
  rosterSport,
  onJerseyClick,
  onRemoveTeam,
  onAddTeam,
}: ProspectMainViewerProps) {
  // `playerLevel == null ||` and not `!playerLevel`: High School is the ordinal 0, and treating
  // it as absent would print "JC 2" on a high schooler.
  const jucoYearLabel =
    prospect.playerLevel == null || isJucoPlayerLevel(prospect.playerLevel)
      ? formatJucoYear(prospect.jucoYear)
      : null;

  return (
    <>
      {/* Header */}
      <div className="prospect-viewer-panel__header">
        <h2 id="prospect-viewer-title" className="prospect-viewer-panel__title">
          {prospect.firstName} {prospect.lastName}
        </h2>
        <div className="prospect-viewer-panel__header-buttons">
          <button
            className="prospect-viewer-panel__favorite-button"
            onClick={onFavoriteToggle}
            aria-label={
              prospect.isFavorite ? "Remove from favorites" : "Add to favorites"
            }
          >
            <StarIcon filled={prospect.isFavorite} />
          </button>
          <button
            className="prospect-viewer-panel__edit-button"
            onClick={onEditClick}
            aria-label="Edit prospect"
          >
            <EditIcon />
          </button>
          <ModalCloseButton onClick={onClose} label="panel" />
        </div>
      </div>

      {/* Teams & Details Section. The details show for every player; only the team pills need a
          team. A player on no team (attached to a roster board from the pool, or whose teams were
          archived) used to lose the whole card, grad year and class included. */}
      <div className="prospect-viewer-panel__section">
        <div className="prospect-viewer-panel__team-card">
          {prospect.teams && prospect.teams.length > 0 && (
            <div className="prospect-viewer-panel__teams-pills-container">
              {prospect.teams.map((team) => (
                <div key={team.id} className="prospect-viewer-panel__team-pill">
                  {team.jerseyNumber !== null && (
                    <span className="prospect-viewer-panel__jersey-number">
                      #{team.jerseyNumber}
                    </span>
                  )}
                  <span className="prospect-viewer-panel__team-name">
                    {team.name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Player details - shown once */}
          <div className="prospect-viewer-panel__details">
            <div className="prospect-viewer-panel__detail-item">
              <span className="prospect-viewer-panel__detail-label">
                Grad Year:
              </span>
              <span className="prospect-viewer-panel__detail-value">
                {prospect.gradYear ?? "-"}
              </span>
            </div>
            {/* Only shown when the roster actually said so. A prospect with no eligibility
                year is the normal case for a recruiting board, and an empty row there would
                read as missing data rather than as not applicable. */}
            {prospect.eligibilityClass && (
              <div className="prospect-viewer-panel__detail-item">
                <span className="prospect-viewer-panel__detail-label">
                  Class:
                </span>
                <span className="prospect-viewer-panel__detail-value">
                  {formatEligibilityClass(prospect.eligibilityClass)}
                </span>
              </div>
            )}
            {/* The JC ladder sits beside the eligibility one rather than replacing it: the two
                disagree by design for a redshirt, so a JUCO player needs both numbers on
                screen. An unset level still shows the year, because a roster import names the
                JC year without ever naming a level. */}
            {jucoYearLabel && (
              <div className="prospect-viewer-panel__detail-item">
                <span className="prospect-viewer-panel__detail-label">
                  JC Year:
                </span>
                <span className="prospect-viewer-panel__detail-value">
                  {jucoYearLabel}
                </span>
              </div>
            )}
            <div className="prospect-viewer-panel__detail-item">
              <span className="prospect-viewer-panel__detail-label">
                Height:
              </span>
              <span className="prospect-viewer-panel__detail-value">
                {prospect.height ?? "-"}
              </span>
            </div>
            <div className="prospect-viewer-panel__detail-item">
              <span className="prospect-viewer-panel__detail-label">
                Weight:
              </span>
              <span className="prospect-viewer-panel__detail-value">
                {prospect.weight ? `${prospect.weight} lbs` : "-"}
              </span>
            </div>
            <div className="prospect-viewer-panel__detail-item">
              <span className="prospect-viewer-panel__detail-label">
                Bats:
              </span>
              <span className="prospect-viewer-panel__detail-value">
                {prospect.bats ?? "-"}
              </span>
            </div>
            <div className="prospect-viewer-panel__detail-item">
              <span className="prospect-viewer-panel__detail-label">
                Throws:
              </span>
              <span className="prospect-viewer-panel__detail-value">
                {prospect.throws ?? "-"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="prospect-viewer-panel__section-nav">
        <button
          className={`prospect-viewer-panel__section-button ${
            activeSection === "notes"
              ? "prospect-viewer-panel__section-button--active"
              : ""
          }`}
          onClick={() => onSectionChange("notes")}
        >
          Notes
        </button>
        <button
          className={`prospect-viewer-panel__section-button ${
            activeSection === "videos"
              ? "prospect-viewer-panel__section-button--active"
              : ""
          }`}
          onClick={() => onSectionChange("videos")}
        >
          Videos
        </button>
        <button
          className={`prospect-viewer-panel__section-button ${
            activeSection === "profile"
              ? "prospect-viewer-panel__section-button--active"
              : ""
          }`}
          onClick={() => onSectionChange("profile")}
        >
          Metrics
        </button>
        <button
          className={`prospect-viewer-panel__section-button ${
            activeSection === "about"
              ? "prospect-viewer-panel__section-button--active"
              : ""
          }`}
          onClick={() => onSectionChange("about")}
        >
          About
        </button>
      </div>

      {/* Profile Section */}
      {activeSection === "profile" && (
        <ProspectProfileSection
          prospect={prospect}
          activeMetricsTab={activeMetricsTab}
          onMetricsTabChange={onMetricsTabChange}
          tagDefinitions={tagDefinitions}
          tagsByName={tagsByName}
          onTagPress={onTagPress}
          tagError={tagError}
          metricsError={metricsError}
          onPositionPlayerMetricUpdate={onPositionPlayerMetricUpdate}
          onPitcherMetricUpdate={onPitcherMetricUpdate}
          pitchConfigs={pitchConfigs}
          currentUserId={currentUserId}
          sport={sport}
          rosterSport={rosterSport}
        />
      )}

      {/* Videos Section */}
      {activeSection === "videos" && (
        <ProspectVideosSection
          prospectId={prospect.id}
          onResetSection={() => onSectionChange("profile")}
        />
      )}

      {/* Notes Section (recruiting notes + documents) */}
      {activeSection === "notes" && (
        <ProspectDocsSection
          prospectId={prospect.id}
          onResetSection={() => onSectionChange("profile")}
        />
      )}

      {/* About Section */}
      {activeSection === "about" && (
        <ProspectAboutSection
          prospect={prospect}
          onJerseyClick={onJerseyClick}
          onRemoveTeam={onRemoveTeam}
          onAddTeam={onAddTeam}
        />
      )}

    </>
  );
}
